import {
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  MixpanelSourceProvider,
  Tour,
  useMixpanel,
  useSecuritiesContext,
  VStack,
} from '@talos/kyoko';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { TourName } from '../../../../tokens/tours';
import type { PriceGridV2PanelConfig } from '../../../../types/LayoutConfig';
import { getIsBBOEnabled } from '../../bbo';
import { expandSecurityToPriceGridRows } from '../../expandSecurityToPriceGridRows';
import { usePriceGridDataObservable, usePriceGridFilter } from '../../hooks';
import { usePriceGridSettings } from '../../hooks/usePriceGridSettings';
import { usePriceGridSourceObservable } from '../../hooks/usePriceGridSourceObservable';
import {
  clampPriceIncrement,
  getLadderType,
  getMinPriceIncrement,
  getPriceIncrement,
  PriceGridLadderType,
  setLadderType,
  setPriceIncrement,
} from '../../ladder';
import { PriceGrid } from '../../PriceGrid';
import { PriceGridBody } from '../../PriceGridHeaderLayout';
import { PriceGridSettingsContext } from '../../PriceGridSettingsContext';
import { getShowRFQ } from '../../rfqVisibility';
import type { PriceGridFilterConfig, PriceGridSourceItem } from '../../types';
import { GENERIC_PRICE_GRID_FILTER_CONFIG } from './filters';
import { GenericPriceGridHeader } from './GenericPriceGridHeader';
import { useGenericPriceGridColumns } from './useGenericPriceGridColumns';

export type GenericPriceGridParameters = Pick<PriceGridV2PanelConfig, 'filterState'>;

export interface GenericPriceGridProps {
  blotterID: string;
  filterConfig?: PriceGridFilterConfig<PriceGridSourceItem>;
  parameters?: GenericPriceGridParameters;
  onParametersChange?: (parameters: Partial<GenericPriceGridParameters>) => void;
}

export function GenericPriceGrid({
  blotterID,
  filterConfig = GENERIC_PRICE_GRID_FILTER_CONFIG,
  parameters,
  onParametersChange,
}: GenericPriceGridProps) {
  const filtersPortalId = `${blotterID}-generic-price-grid-filters`;
  const sourceObservable = usePriceGridSourceObservable();
  const { securitiesListSortedByRank } = useSecuritiesContext();
  const mixpanel = useMixpanel(MixpanelEventSource.PriceGrid);
  const { filterState, setFilterState, setSearch, filterSourceItems } = usePriceGridFilter({
    filterConfig,
    filterState: parameters?.filterState,
    onFilterStateChanged: nextFilterState => onParametersChange?.({ filterState: nextFilterState }),
  });

  const ladderType = getLadderType(filterState);
  const priceIncrement = getPriceIncrement(filterState);
  const initialLadderTypeRef = useRef(ladderType);

  const filteredSecurities = useMemo(
    () =>
      filterSourceItems(securitiesListSortedByRank.map(security => ({ security }))).map(
        sourceItem => sourceItem.security
      ),
    [filterSourceItems, securitiesListSortedByRank]
  );
  const minPriceIncrement = useMemo(() => getMinPriceIncrement(filteredSecurities), [filteredSecurities]);
  const resolvedPriceIncrement = useMemo(
    () => clampPriceIncrement(priceIncrement, minPriceIncrement),
    [minPriceIncrement, priceIncrement]
  );

  useEffect(() => {
    if (resolvedPriceIncrement !== priceIncrement) {
      setFilterState(setPriceIncrement(filterState, resolvedPriceIncrement));
    }
  }, [filterState, priceIncrement, resolvedPriceIncrement, setFilterState]);

  useEffect(() => {
    mixpanel.track(MixpanelEvent.ViewPriceGridLadderState, {
      [MixpanelEventProperty.Type]: initialLadderTypeRef.current === PriceGridLadderType.Book ? 'Book' : 'Ladder',
    });
    // Track current ladder state once when the grid is opened.
  }, [mixpanel]);

  const expandRows = useCallback(
    (sourceItem: PriceGridSourceItem) => expandSecurityToPriceGridRows(sourceItem, { ladderType }),
    [ladderType]
  );

  const dataObservable = usePriceGridDataObservable({
    sourceObservable,
    filterSourceItems,
    expandRows,
  });
  const { orderPresetID, subAccount } = filterState;
  const isBBOEnabled = getIsBBOEnabled(filterState);
  const showRFQ = getShowRFQ(filterState);
  const ladderConfig = useMemo(
    () => ({ ladderType, priceIncrement: resolvedPriceIncrement }),
    [ladderType, resolvedPriceIncrement]
  );
  const { clickToTradePreviewOrder, columns, maxOrderSizeDialogs, priceGridMarketDataHandlers } =
    useGenericPriceGridColumns({
      isBBOEnabled,
      ladderConfig,
      primingContext: { orderPresetID, subAccount },
      showRFQ,
    });

  const priceGridSettings = usePriceGridSettings(filterState, setFilterState, MixpanelEventSource.PriceGrid);

  const handleLadderTypeChange = useCallback(
    (nextLadderType: typeof ladderType) => {
      if (nextLadderType === ladderType) {
        return;
      }

      mixpanel.track(
        nextLadderType === PriceGridLadderType.Book
          ? MixpanelEvent.TogglePriceGridLadderToPrice
          : MixpanelEvent.TogglePriceGridLadderToVolume
      );
      setFilterState(setLadderType(filterState, nextLadderType));
    },
    [filterState, ladderType, mixpanel, setFilterState]
  );

  const handlePriceIncrementChange = useCallback(
    (increment: string) => {
      const clampedIncrement = clampPriceIncrement(increment, minPriceIncrement);
      if (clampedIncrement === resolvedPriceIncrement) {
        return;
      }

      mixpanel.track(MixpanelEvent.ChangePriceGridLadderIncrement, {
        [MixpanelEventProperty.Value]: clampedIncrement,
      });
      setFilterState(setPriceIncrement(filterState, clampedIncrement));
    },
    [filterState, minPriceIncrement, mixpanel, resolvedPriceIncrement, setFilterState]
  );

  return (
    <VStack h="100%" w="100%" alignItems="flex-start" data-testid="price-grid-showcase-panel">
      <Tour tour={TourName.v100PriceGrid} delay={1000} />
      <GenericPriceGridHeader
        filtersPortalId={filtersPortalId}
        filterState={filterState}
        onFilterStateChanged={setFilterState}
        ladderType={ladderType}
        priceIncrement={resolvedPriceIncrement}
        minPriceIncrement={minPriceIncrement}
        onLadderTypeChange={handleLadderTypeChange}
        onPriceIncrementChange={handlePriceIncrementChange}
      />
      <PriceGridBody>
        <MixpanelSourceProvider value={MixpanelEventSource.PriceGrid}>
          <PriceGridSettingsContext.Provider value={priceGridSettings}>
            <PriceGrid
              blotterID={blotterID}
              dataObservable={dataObservable}
              columns={columns}
              filtersPortalId={filtersPortalId}
              search={filterState.search}
              onSearchChange={setSearch}
              supportsFavorites
              priceGridMarketDataHandlers={priceGridMarketDataHandlers}
              clickToTradePreviewOrder={clickToTradePreviewOrder}
              maxOrderSizeDialogs={maxOrderSizeDialogs}
              ladderConfig={ladderConfig}
            />
          </PriceGridSettingsContext.Provider>
        </MixpanelSourceProvider>
      </PriceGridBody>
    </VStack>
  );
}
